/* global React, Icon, Modal, Button, Select, toast, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · Kategori-dialog
// Åpnes fra en forbrukskategori. Går gjennom, sorterer og flytter
// importerte transaksjoner til andre kategorier via backend
// (updatePersonalFinanceTransactionCategory).
// Analysen øverst regnes fra de faktiske transaksjonene.
// ============================================================
const { useState: pkUseState, useMemo: pkUseMemo } = React;

const PK_SORTS = [
  { value: 'date-desc' },
  { value: 'date-asc' },
  { value: 'amt-desc' },
  { value: 'amt-asc' },
];

// Generelle kutteforslag per kategoritype. Rene råd, ingen påstander om brukerens data.
const PK_TIPS = [
  [/bolig|leie|husleie|strøm/i, ['pkn.kat.tips.housing.1', 'pkn.kat.tips.housing.2', 'pkn.kat.tips.housing.3']],
  [/mat|dagligvare/i, ['pkn.kat.tips.food.1', 'pkn.kat.tips.food.2', 'pkn.kat.tips.food.3']],
  [/shopping/i, ['pkn.kat.tips.shopping.1', 'pkn.kat.tips.shopping.2', 'pkn.kat.tips.shopping.3']],
  [/transport/i, ['pkn.kat.tips.transport.1', 'pkn.kat.tips.transport.2', 'pkn.kat.tips.transport.3']],
  [/restaurant|uteliv/i, ['pkn.kat.tips.dining.1', 'pkn.kat.tips.dining.2', 'pkn.kat.tips.dining.3']],
  [/reise/i, ['pkn.kat.tips.travel.1', 'pkn.kat.tips.travel.2', 'pkn.kat.tips.travel.3']],
  [/forsikring/i, ['pkn.kat.tips.insurance.1', 'pkn.kat.tips.insurance.2', 'pkn.kat.tips.insurance.3']],
  [/abonnement/i, ['pkn.kat.tips.subscriptions.1', 'pkn.kat.tips.subscriptions.2', 'pkn.kat.tips.subscriptions.3']],
];
const pkTips = (name) => (PK_TIPS.find(([pattern]) => pattern.test(name || '')) || [null, ['pkn.kat.tips.default.1', 'pkn.kat.tips.default.2', 'pkn.kat.tips.default.3']])[1].map((key) => t(key));

const pkDate = (value) => {
  const parts = String(value || '').slice(0, 10).split('-');
  return parts.length === 3 ? `${Number(parts[2])}.${Number(parts[1])}.${parts[0]}` : '';
};
const pkAmount = (transaction) => Math.abs(Number(transaction?.amount || 0));
const pkSource = (transaction) => transaction?.sourceFileName || transaction?.fileName || transaction?.sourceName || t('pkn.kat.importedStatement');
// «Rema 1000 Majorstuen 4412» → «Rema 1000 Majorstuen»: grupperer samme brukersted.
const pkBaseName = (name) => String(name || '').replace(/\s+\d{2,}$/, '').trim() || t('pkn.kat.unknown');

function pkAnalysis(rows, categoryName, changePct) {
  const total = rows.reduce((sum, row) => sum + pkAmount(row), 0);
  const average = total / Math.max(1, rows.length);
  const map = new Map();
  rows.forEach((row) => {
    const key = pkBaseName(row.description);
    const entry = map.get(key) || { count: 0, name: key, sum: 0 };
    entry.sum += pkAmount(row);
    entry.count += 1;
    map.set(key, entry);
  });
  const merchants = [...map.values()].sort((a, b) => b.sum - a.sum).slice(0, 3)
    .map((entry) => ({ ...entry, share: total ? Math.round(entry.sum / total * 100) : 0 }));
  const unusual = rows.filter((row) => pkAmount(row) > average * 1.7).sort((a, b) => pkAmount(b) - pkAmount(a)).slice(0, 2);
  const potential = total ? Math.max(50, Math.round(total * (changePct > 0 ? 0.15 : 0.08) / 50) * 50) : 0;
  return { average, merchants, potential, tips: pkTips(categoryName), total, unusual };
}

function PoCatModal({ category, categories = [], changePct = 0, onClose, onMove, transactions = [] }) {
  const [selected, setSelected] = pkUseState(() => new Set());
  const [sort, setSort] = pkUseState('date-desc');
  const [target, setTarget] = pkUseState(() => (categories.find((name) => name !== category) || 'Annet'));
  const [learn, setLearn] = pkUseState(true);
  const [query, setQuery] = pkUseState('');
  const [saving, setSaving] = pkUseState(false);

  const filtered = pkUseMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return transactions;
    return transactions.filter((row) => `${row.description || ''} ${pkDate(row.date)} ${pkSource(row)}`.toLowerCase().includes(needle));
  }, [transactions, query]);

  const sorted = pkUseMemo(() => {
    const rows = [...filtered];
    if (sort === 'date-desc') rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    else if (sort === 'date-asc') rows.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    else if (sort === 'amt-desc') rows.sort((a, b) => pkAmount(b) - pkAmount(a));
    else rows.sort((a, b) => pkAmount(a) - pkAmount(b));
    return rows;
  }, [filtered, sort]);

  const allSelected = sorted.length > 0 && sorted.every((row) => selected.has(row.id));
  const toggle = (id) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sorted.map((row) => row.id).filter(Boolean)));

  const move = async () => {
    if (!selected.size || !target || target === category) return;
    setSaving(true);
    try {
      await onMove?.({ category: target, learn, transactionIds: Array.from(selected) });
      setSelected(new Set());
    } finally {
      setSaving(false);
    }
  };

  const targetOptions = pkUseMemo(() => {
    const options = categories.filter((name) => name !== category);
    ['Annet', 'Sparing og investering'].forEach((name) => { if (!options.includes(name)) options.push(name); });
    return options;
  }, [categories, category]);

  const analysis = pkUseMemo(() => pkAnalysis(transactions, category, changePct), [transactions, category, changePct, PlummeI18n.getLocale()]);
  const avatar = (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
  const top = analysis.merchants[0];
  const lead = transactions.length === 0
    ? t('pkn.kat.leadEmpty', { category })
    : `${t(transactions.length === 1 ? 'pkn.kat.leadOne' : 'pkn.kat.leadMany', { category, total: fmt(analysis.total), count: transactions.length, average: fmt(Math.round(analysis.average)) })}${top ? ` ${t('pkn.kat.topItem', { name: top.name, amount: fmt(top.sum) })}` : ''}${changePct ? ` ${t(changePct > 0 ? 'pkn.kat.changeUp' : 'pkn.kat.changeDown', { pct: Math.abs(Math.round(changePct)) })}` : ''}`;

  return <Modal open onClose={onClose} title={category} max={760}
    titleRight={<span className="pk-count">{t(transactions.length === 1 ? 'pkn.kat.lineOne' : 'pkn.kat.lineMany', { count: transactions.length })}</span>}
    footer={<React.Fragment>
      <button className="pk-close-link" onClick={onClose} disabled={saving}>{t('common.close')}</button>
      <Button icon="arrowRight" disabled={saving || !selected.size || !target || target === category} onClick={move}>
        {saving ? t('pkn.kat.moving') : `${t('pkn.kat.move')}${selected.size ? ` ${selected.size}` : ''}`}
      </Button>
    </React.Fragment>}>
    <div className="pk-ai">
      <div className="pk-ai-top">
        <span className="pk-ai-ava"><img src={avatar} alt="SuperPlumma" /></span>
        <div className="pk-ai-hd"><div className="pk-ai-title">{t('po.analysis')}</div><div className="pk-ai-sub">{t('po.analysisSub')}</div></div>
        {!!changePct && <span className="pk-ai-chg" style={{ color: changePct > 0 ? 'var(--warning)' : 'var(--success)', background: changePct > 0 ? 'var(--warning-bg)' : 'var(--success-bg)' }}>{changePct > 0 ? '+' : ''}{Math.round(changePct)}&nbsp;% {t('pkn.kat.vsPrev')}</span>}
      </div>
      <p className="pk-ai-lead">{lead}</p>
      <div className="pk-ai-cols">
        <div className="pk-ai-block">
          <div className="pk-ai-bt"><Icon name="receipt" size={15} />{t('po.whoIsBehind')}</div>
          {analysis.merchants.length
            ? analysis.merchants.map((merchant) => <div key={merchant.name} className="pk-ai-merch">
              <span className="pk-ai-merch-name">{merchant.name}</span>
              <span className="pk-ai-merch-bar"><span style={{ width: `${merchant.share / (analysis.merchants[0].share || 1) * 100}%` }} /></span>
              <span className="pk-ai-merch-amt">{fmt(merchant.sum)}</span>
            </div>)
            : <div className="pk-ai-none"><Icon name="info" size={15} />{t('po.noTransactionsToAnalyse')}</div>}
        </div>
        <div className="pk-ai-block">
          <div className="pk-ai-bt"><Icon name="alertCircle" size={15} />{t('po.unusualPurchases')}</div>
          {analysis.unusual.length
            ? analysis.unusual.map((row) => <div key={row.id} className="pk-ai-odd">
              <div className="pk-ai-odd-l"><div className="pk-ai-odd-name">{row.description || t('pkn.ctl.transaction')}</div><div className="pk-ai-odd-meta">{pkDate(row.date)} · {t('pkn.kat.timesAverage', { factor: Math.round(pkAmount(row) / (analysis.average || 1) * 10) / 10 })}</div></div>
              <span className="pk-ai-odd-amt">{fmt(pkAmount(row))}</span>
            </div>)
            : <div className="pk-ai-none"><Icon name="checkCircle" size={15} />{t('po.noUnusualPurchases')}</div>}
        </div>
      </div>
      <div className="pk-ai-block pk-ai-actions">
        <div className="pk-ai-bt"><Icon name="sparkle" size={15} />{t('pkn.kat.howToCut')}{analysis.potential > 0 && <span className="pk-ai-pot">{t('pkn.kat.potential', { amount: fmt(analysis.potential) })}</span>}</div>
        <ul className="pk-ai-tips">{analysis.tips.map((tip, index) => <li key={index}><Icon name="check" size={14} />{tip}</li>)}</ul>
      </div>
    </div>

    <div className="pk-controls">
      <div className="pk-controls-top">
        <button className={`pk-selall${allSelected ? ' on' : ''}`} onClick={toggleAll}>{allSelected ? t('pkn.kat.clearAll') : t('pkn.kat.selectAll')}</button>
        <label className="pk-learn">
          <span data-app-control="true" className={`pk-chk${learn ? ' on' : ''}`} onClick={(event) => { event.preventDefault(); setLearn((value) => !value); }}>{learn && <Icon name="check" size={14} />}</span>
          {t('pkn.kat.learn')}
        </label>
        <div className="pk-search">
          <Icon name="search" size={17} />
          <input value={query} placeholder={t('po.searchTransactions')} onChange={(event) => setQuery(event.target.value)} />
          {query && <button className="pk-search-clear" onClick={() => setQuery('')} aria-label={t('po.clearSearch')}><Icon name="x" size={14} /></button>}
        </div>
      </div>
      <div className="pk-selects">
        <label className="pk-sel"><span>{t('po.sort')}</span><Select value={sort} onChange={(event) => setSort(event.target.value)}>{PK_SORTS.map((option) => <option key={option.value} value={option.value}>{t('pkn.kat.sort.' + option.value)}</option>)}</Select></label>
        <label className="pk-sel"><span>{t('inbox.moveTo')}</span><Select value={target} onChange={(event) => setTarget(event.target.value)}>{targetOptions.map((name) => <option key={name} value={name}>{name}</option>)}</Select></label>
      </div>
    </div>

    <div className="pk-list">
      {sorted.length === 0
        ? <div className="pk-empty"><Icon name={transactions.length === 0 ? 'info' : 'search'} size={22} />{transactions.length === 0 ? t('pkn.kat.noTxInCategory') : t('pkn.kat.noTxMatchSearch')}</div>
        : sorted.map((row) => {
          const on = selected.has(row.id);
          return <button key={row.id} className={`pk-row${on ? ' on' : ''}`} onClick={() => toggle(row.id)}>
            <span className={`pk-chk${on ? ' on' : ''}`}>{on && <Icon name="check" size={14} />}</span>
            <div className="pk-row-main">
              <div className="pk-row-name">{row.description || t('pkn.ctl.transaction')}</div>
              <div className="pk-row-meta">{pkDate(row.date)} · {pkSource(row)}</div>
            </div>
            <span className="pk-row-amt">{fmt(pkAmount(row))}</span>
          </button>;
        })}
    </div>
  </Modal>;
}

Object.assign(window, { PoCatModal });
